import React, { useState, useCallback, useEffect } from 'react';
import EditItemModal from './EditItemModal';
import './Cart.css';
import { useTranslation } from '../../context/LanguageContext';
import axios from '../../utils/axios';
import { formatCurrency } from '../../utils/formatters';
import CheckoutModal from './payment/CheckoutModal';

const Cart = ({ items, onRemoveItem, onUpdateItem, onClearCart, onOrderCompleted, loading, receiptPrinterRef }) => {
  const { t, isRTL } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editMode, setEditMode] = useState(null); // 'quantity' or 'price'
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const calculateTotals = useCallback(() => {
    if (!settings) return { subtotal: 0, tax: 0, total: 0 };

    const taxRate = settings.tax.percentage / 100;
    
    // Calculate subtotal with tax (sum of all item prices × quantities)
    const subtotalWithTax = items.reduce((sum, item) => {
      const price = parseFloat(item.price || 0);
      const quantity = Math.max(1, parseFloat(item.quantity || 1));
      return sum + (price * quantity);
    }, 0);
    
    // Calculate subtotal without tax (divide by 1+taxRate to remove tax)
    const subtotalWithoutTax = subtotalWithTax / (1 + taxRate);
    
    // Calculate tax amount (subtract tax-exclusive from tax-inclusive subtotal)
    const tax = subtotalWithTax - subtotalWithoutTax;
    
    // Ensure values are non-negative and properly rounded
    const finalSubtotalWithoutTax = Math.max(0, Math.round(subtotalWithoutTax * 100) / 100);
    const finalTaxAmount = Math.max(0, Math.round(tax * 100) / 100);
    const finalTotalAmount = Math.max(0, Math.round(subtotalWithTax * 100) / 100);
    
    return {
      subtotal: finalSubtotalWithoutTax,
      tax: finalTaxAmount,
      total: finalTotalAmount
    }
  }, [items, settings]);

  const handleRemoveItem = useCallback((itemId, e) => {
    if (e) e.stopPropagation();
    if (loading) return;
    onRemoveItem(itemId);
  }, [onRemoveItem, loading]);

  const handleCheckout = useCallback(async () => {
    // Show payment method selection modal
    setShowPaymentModal(true);
  }, []);

  const handlePaymentConfirm = useCallback(async (orderData) => {
    setShowPaymentModal(false);
    
    try {
      // Create order with selected payment method and company info if applicable
      const order = {
        items: items.map(item => ({
          item_id: item.id,
          quantity: item.quantity,
          unit_price: item.customPrice || item.price, // Use customPrice if available
          subtotal: (item.customPrice || item.price) * item.quantity
        })),
        order_type: 'takeaway',
        status: 'pending',
        payment_method: orderData.payment_method,
        customer_type: orderData.customer_type,
        company_name: orderData.company_name,
        company_tax_number: orderData.company_tax_number,
        delivery_fee: 0,
        ...calculateTotals()
      };
      
      const response = await axios.post('/orders', order);
      const newOrder = response.data.data;

      // Ensure company info is explicitly passed to receipt printer
      if (receiptPrinterRef?.current) {
        const orderWithCompanyInfo = {
          ...newOrder,
          customer_type: newOrder.customer_type || orderData.customer_type,
          company_name: newOrder.company_name || orderData.company_name,
          company_tax_number: newOrder.company_tax_number || orderData.company_tax_number
        };
        
        receiptPrinterRef.current.printReceipt(orderWithCompanyInfo);
      }

      // Clear cart
      onClearCart();
      onOrderCompleted?.();

    } catch (error) {
      console.error('Error creating order:', error);
      alert(t('cashier.payment.errors.create_order'));
    }
  }, [items, calculateTotals, onClearCart, onOrderCompleted, receiptPrinterRef, t]);

  const totals = calculateTotals();

  if (!settings) {
    return (
      <div className={`cart ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="cart-header">
          <h2>{t('cashier.cart.title')}</h2>
        </div>
        <div className="cart-items">
          <div className="cart-loading">{t('common.messages.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`cart ${isRTL ? 'rtl' : 'ltr'}`} onClick={e => e.stopPropagation()}>
      <div className="cart-header">
        <h2>{t('cashier.cart.title')}</h2>
      </div>

      <div className="cart-items">
        {items.length === 0 ? (
          <div className="cart-empty">{t('cashier.cart.empty')}</div>
        ) : (
          items.map((item) => (
            <div key={item.id} className="cart-item">
              <div 
                className="item-quantity"
                onClick={() => {
                  if (!loading) {
                    setEditingItem(item);
                    setEditMode('quantity');
                  }
                }}
              >{item.quantity}</div>
              <div className="item-name">{isRTL ? item.name_ar : item.name}</div>
              <div 
                className="item-price"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!loading) {
                    setEditingItem(item);
                    setEditMode('price');
                  }
                }}
              >
                {formatCurrency(item.price * item.quantity, settings.interface.currency_symbol)}
              </div>
              <button
                className="remove-item"
                onClick={(e) => handleRemoveItem(item.id, e)}
                aria-label={t('cashier.cart.remove_item')}
                disabled={loading}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      {items.length > 0 && (
        <div className="cart-totals">
          <div className="subtotal">
            <span>{t('cashier.cart.subtotal')}</span>
            <span>{formatCurrency(totals.subtotal, settings.interface.currency_symbol)}</span>
          </div>
          <div className="tax">
            <span>{t('cashier.cart.tax')} ({settings.tax.percentage}%)</span>
            <span>{formatCurrency(totals.tax, settings.interface.currency_symbol)}</span>
          </div>
          <div className="total">
            <span>{t('cashier.cart.total')}</span>
            <span>{formatCurrency(totals.total, settings.interface.currency_symbol)}</span>
          </div>
        </div>
      )}

      <div className="cart-footer">
        <button
          className="clear-cart-button"
          onClick={onClearCart}
          disabled={items.length === 0 || loading}
        >
          {t('cashier.cart.clear')}
        </button>
        
        <button
          className="checkout-button"
          onClick={handleCheckout}
          disabled={items.length === 0 || loading}
        >
          {loading ? t('cashier.payment.actions.processing') : t('cashier.cart.checkout')}
        </button>
      </div>
      {editingItem && editMode && (
        <EditItemModal
          item={editingItem}
          mode={editMode}
          onClose={() => {
            setEditingItem(null);
            setEditMode(null);
          }}
          onSave={(updatedItem) => {
            onUpdateItem(updatedItem);
            setEditingItem(null);
            setEditMode(null);
          }}
        />
      )}
      {/* Checkout Modal */}
      {showPaymentModal && (
        <CheckoutModal 
          onClose={() => setShowPaymentModal(false)}
          onConfirm={handlePaymentConfirm}
        />
      )}
    </div>
  );
};

export default Cart;
